import { Link } from 'react-router-dom';
import { initialen } from '@vip/shared';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

/**
 * Wie er op iets zit, als een rijtje avatars met de initialen erin.
 *
 * Namen uitschrijven kost een regel per mens en dat past niet naast een
 * opdracht; een aantal ("7 mensen") zegt niet wíe. Een avatar is klein genoeg om
 * er een stuk of vijf naast elkaar te zetten en toch te zien om wie het gaat, en
 * wie hem aanwijst leest de naam.
 *
 * Boven het maximum een laatste rondje met een teller. Dat is een grens aan de
 * breedte en niet aan de informatie: de hele lijst staat in de tooltip daarvan.
 */
export function PersonenStapel({
  personen,
  maximum = 5,
  klikbaar = true,
}: {
  personen: readonly { id: string; naam: string }[];
  maximum?: number;
  /** Uit als de stapel zelf al in een link staat: een link in een link is geen
   *  geldige HTML en de klik zou tegen elkaar in werken. */
  klikbaar?: boolean;
}) {
  // Niets als er niemand op zit: een lege plek is duidelijk genoeg, en het woord
  // "niemand" naast elke lege opdracht maakt de lijst onrustig.
  if (personen.length === 0) return null;

  const zichtbaar = personen.slice(0, maximum);
  const rest = personen.slice(maximum);

  // De provider hier en niet één keer rond de hele app: dit is voorlopig de
  // enige plek met tooltips, en dan hoort de afhankelijkheid ook hier.
  return (
    <TooltipProvider>
      <div className="flex items-center -space-x-2">
        {zichtbaar.map((p) => (
          <Tooltip key={p.id}>
            <TooltipTrigger asChild>
              {klikbaar ? (
                <Link
                  aria-label={p.naam}
                  className="ring-background rounded-full ring-2 transition-transform hover:z-10 hover:scale-110"
                  to={`/personen/${p.id}`}
                >
                  <Avatar className="size-7">
                    <AvatarFallback className="text-xs">{initialen(p.naam)}</AvatarFallback>
                  </Avatar>
                </Link>
              ) : (
                <Avatar aria-label={p.naam} className="ring-background size-7 ring-2">
                  <AvatarFallback className="text-xs">{initialen(p.naam)}</AvatarFallback>
                </Avatar>
              )}
            </TooltipTrigger>
            <TooltipContent>{p.naam}</TooltipContent>
          </Tooltip>
        ))}

        {rest.length > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Avatar className="size-7 ring-2 ring-background">
                <AvatarFallback className="text-muted-foreground text-xs">
                  +{rest.length}
                </AvatarFallback>
              </Avatar>
            </TooltipTrigger>
            <TooltipContent>{rest.map((p) => p.naam).join(', ')}</TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
}
